/**
 * Operations Dashboard — Admin landing page
 * Desktop-first overview of approvals, compliance, trips, and fleet status.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { getAllPilotsStatus } from '@/services/dutyTimeService';
import type { PilotDutyStatus } from '@/services/dutyTimeService';
import { useAuth } from '@/contexts/AuthContext';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  ShieldExclamationIcon,
  WrenchScrewdriverIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

// ── Types ────────────────────────────────────────────────────────────────────

interface Trip {
  id: number;
  qNumber: string;
  startDate: string;
  endDate: string;
  tailNumber?: string;
  status: string;
  flights: Array<{ id: number; status: string; fromAirport?: string; toAirport?: string; legNumber: number }>;
  crewMembers: Array<{ user: { firstName: string; lastName: string } }>;
}

interface ExpiringItem {
  type: string;
  pilotId: number;
  pilotName: string;
  description: string;
  expiryDate: string;
  daysRemaining: number;
  severity: 'expired' | 'critical' | 'warning' | 'ok';
}

interface ActiveMel {
  id: number;
  aircraftId: number;
  melItem: { itemNumber: string; title: string; repairCategory: string };
  aircraft?: { tailNumber: string };
  deferredDate: string;
  expiryDate?: string;
}

interface SafetyReportSummary {
  id: number;
  anonymous: boolean;
  hazardCategory: string;
  severity: string;
  likelihood: string;
  narrative: string;
  fromAirport?: string;
  toAirport?: string;
  tailNumber?: string;
  status: string;
  createdAt: string;
  reporter?: { firstName: string; lastName: string } | null;
  trip?: { qNumber: string } | null;
}

interface SafetyStats {
  open: number;
  underReview: number;
  closed: number;
  total: number;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function OpsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [trips, setTrips] = useState<Trip[]>([]);
  const [expiringItems, setExpiringItems] = useState<ExpiringItem[]>([]);
  const [activeMels, setActiveMels] = useState<ActiveMel[]>([]);
  const [safetyReports, setSafetyReports] = useState<SafetyReportSummary[]>([]);
  const [safetyStats, setSafetyStats] = useState<SafetyStats>({ open: 0, underReview: 0, closed: 0, total: 0 });
  const [dutyStatuses, setDutyStatuses] = useState<PilotDutyStatus[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tripsRes, expiringRes, melsRes, reportsRes, statsRes, dutyRes] = await Promise.allSettled([
        api.get<{ data: Trip[] }>('/trips'),
        api.get<{ data: ExpiringItem[] }>('/qualifications/expiring?days=90'),
        api.get<{ data: ActiveMel[] }>('/mel/active'),
        api.get<{ data: SafetyReportSummary[] }>('/safety-reports'),
        api.get<{ data: SafetyStats }>('/safety-reports/stats'),
        getAllPilotsStatus(),
      ]);

      if (tripsRes.status === 'fulfilled') setTrips(tripsRes.value.data);
      if (expiringRes.status === 'fulfilled') setExpiringItems(expiringRes.value.data);
      if (melsRes.status === 'fulfilled') setActiveMels(melsRes.value.data);
      if (reportsRes.status === 'fulfilled') setSafetyReports(reportsRes.value.data);
      if (statsRes.status === 'fulfilled') setSafetyStats(statsRes.value.data);
      if (dutyRes.status === 'fulfilled') setDutyStatuses(dutyRes.value);
    } catch {
      // Individual failures handled by allSettled
    } finally {
      setLoading(false);
    }
  };

  // ── Derived data ──

  const pendingApprovals = trips.flatMap(t =>
    t.flights.filter(f => f.status === 'SUBMITTED').map(f => ({ ...f, trip: t }))
  );

  const activeTrips = trips.filter(t => t.status === 'PENDING_REVIEW' || t.status === 'IN_PROGRESS');
  const completedTrips = trips.filter(t => t.status === 'COMPLETED');

  const dutyAlertsCount = dutyStatuses.filter(s => s.overallStatus !== 'green').length;
  const dutyRedCount = dutyStatuses.filter(s => s.overallStatus === 'red').length;

  const expiredItems = expiringItems.filter(i => i.severity === 'expired');
  const criticalItems = expiringItems.filter(i => i.severity === 'critical');
  const warningItems = expiringItems.filter(i => i.severity === 'warning');

  const now = new Date();
  const formatDate = (d: string) => {
    const [y, m, day] = d.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };

  // ── Render ──

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">
            Good {now.getHours() < 12 ? 'morning' : now.getHours() < 17 ? 'afternoon' : 'evening'}, {user?.firstName}
          </h1>
          <p className="text-muted-foreground mt-1">Operations overview — {now.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <KpiCard
            icon={<CheckCircleIcon className="w-6 h-6" />}
            label="Pending Approvals"
            value={pendingApprovals.length}
            color={pendingApprovals.length > 0 ? 'amber' : 'green'}
            onClick={() => navigate('/approvals')}
          />
          <KpiCard
            icon={<PaperAirplaneIcon className="w-6 h-6" />}
            label="Active Trips"
            value={activeTrips.length}
            color="blue"
            onClick={() => navigate('/trips')}
          />
          <KpiCard
            icon={<ShieldExclamationIcon className="w-6 h-6" />}
            label="Safety Reports"
            value={safetyStats.open + safetyStats.underReview}
            subtitle={safetyStats.total > 0 ? `${safetyStats.total} total` : undefined}
            color={safetyStats.open > 0 ? 'red' : 'green'}
            onClick={() => navigate('/safety-reports')}
          />
          <KpiCard
            icon={<ExclamationTriangleIcon className="w-6 h-6" />}
            label="Expiry Alerts"
            value={expiredItems.length + criticalItems.length}
            subtitle={warningItems.length > 0 ? `+${warningItems.length} warning` : undefined}
            color={expiredItems.length > 0 ? 'red' : criticalItems.length > 0 ? 'amber' : 'green'}
            onClick={() => navigate('/admin?tab=users')}
          />
          <KpiCard
            icon={<ClockIcon className="w-6 h-6" />}
            label="Duty Alerts"
            value={dutyAlertsCount}
            subtitle={dutyRedCount > 0 ? `${dutyRedCount} exceeded` : undefined}
            color={dutyRedCount > 0 ? 'red' : dutyAlertsCount > 0 ? 'amber' : 'green'}
            onClick={() => navigate('/duty-time')}
          />
          <KpiCard
            icon={<WrenchScrewdriverIcon className="w-6 h-6" />}
            label="Active MELs"
            value={activeMels.length}
            color={activeMels.length > 0 ? 'amber' : 'green'}
            onClick={() => navigate('/mel/active')}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left column: Approvals + Active Trips */}
          <div className="lg:col-span-2 space-y-6">

            {/* Approval Queue */}
            <div className="bg-card border border-border rounded-lg">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <ClockIcon className="w-5 h-5 text-amber-500" />
                  Approval Queue
                </h2>
                {pendingApprovals.length > 0 && (
                  <button onClick={() => navigate('/approvals')} className="text-xs text-primary hover:text-primary/80 font-medium flex items-center gap-0.5">
                    Review all <ChevronRightIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="divide-y divide-border">
                {pendingApprovals.length === 0 ? (
                  <div className="px-5 py-8 text-center text-muted-foreground text-sm">
                    No flights waiting for approval
                  </div>
                ) : pendingApprovals.slice(0, 5).map((f) => (
                  <div key={f.id} className="px-5 py-3 flex items-center justify-between hover:bg-muted/30 cursor-pointer"
                    onClick={() => navigate(`/trips/${f.trip.id}`)}>
                    <div>
                      <span className="font-medium text-foreground text-sm">{f.trip.qNumber}</span>
                      <span className="text-muted-foreground text-sm ml-2">Leg {f.legNumber}: {f.fromAirport} → {f.toAirport}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">
                        {f.trip.crewMembers.map(cm => cm.user.lastName).join(', ')}
                      </span>
                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                        Submitted
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Trips */}
            <div className="bg-card border border-border rounded-lg">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <PaperAirplaneIcon className="w-5 h-5 text-blue-500" />
                  Active Trips
                </h2>
                <button onClick={() => navigate('/trips')} className="text-xs text-primary hover:text-primary/80 font-medium flex items-center gap-0.5">
                  View all <ChevronRightIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="divide-y divide-border">
                {activeTrips.length === 0 ? (
                  <div className="px-5 py-8 text-center text-muted-foreground text-sm">
                    No active trips
                  </div>
                ) : activeTrips.slice(0, 6).map((trip) => (
                  <div key={trip.id} className="px-5 py-3 flex items-center justify-between hover:bg-muted/30 cursor-pointer"
                    onClick={() => navigate(`/trips/${trip.id}`)}>
                    <div className="flex items-center gap-4">
                      <span className="font-mono font-medium text-foreground text-sm">{trip.qNumber}</span>
                      <span className="text-muted-foreground text-sm">{trip.tailNumber}</span>
                      <span className="text-muted-foreground text-xs">
                        {formatDate(trip.startDate)} – {formatDate(trip.endDate)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">
                        {trip.flights.length} legs &middot; {trip.crewMembers.map(cm => cm.user.lastName).join(', ')}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        trip.status === 'IN_PROGRESS'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {trip.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right column: Safety + Compliance + Fleet */}
          <div className="space-y-6">

            {/* Safety Reports */}
            <div className="bg-card border border-border rounded-lg">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <ShieldExclamationIcon className="w-5 h-5 text-amber-500" />
                  Safety Reports
                </h2>
                {safetyStats.total > 0 && (
                  <button onClick={() => navigate('/safety-reports')} className="text-xs text-primary hover:text-primary/80 font-medium flex items-center gap-0.5">
                    View all <ChevronRightIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="divide-y divide-border">
                {safetyReports.length === 0 ? (
                  <div className="px-5 py-8 text-center text-sm">
                    <span className="text-green-600 dark:text-green-400 font-medium">No reports filed</span>
                    <p className="text-muted-foreground text-xs mt-1">Safety reports from pilots will appear here</p>
                  </div>
                ) : safetyReports.slice(0, 5).map((report) => {
                  const riskColors: Record<string, string> = {
                    NEGLIGIBLE: 'bg-green-500', MINOR: 'bg-yellow-500', MAJOR: 'bg-orange-500',
                    HAZARDOUS: 'bg-red-500', CATASTROPHIC: 'bg-red-700',
                  };
                  const statusColors: Record<string, string> = {
                    OPEN: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
                    UNDER_REVIEW: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
                    CLOSED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
                  };
                  return (
                    <div key={report.id} className="px-5 py-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full ${riskColors[report.severity] || 'bg-muted'}`} />
                          <span className="text-sm font-medium text-foreground">
                            {report.hazardCategory.replace('_', ' ')}
                          </span>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${statusColors[report.status] || ''}`}>
                          {report.status.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{report.narrative}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        {report.trip && <span>{report.trip.qNumber}</span>}
                        {report.fromAirport && report.toAirport && (
                          <span>{report.fromAirport} → {report.toAirport}</span>
                        )}
                        <span>{report.anonymous ? 'Anonymous' : report.reporter ? `${report.reporter.firstName} ${report.reporter.lastName}` : ''}</span>
                        <span className="ml-auto">{new Date(report.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Compliance Alerts */}
            <div className="bg-card border border-border rounded-lg">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <ShieldExclamationIcon className="w-5 h-5 text-red-500" />
                  Compliance
                </h2>
              </div>
              <div className="divide-y divide-border">
                {expiringItems.length === 0 ? (
                  <div className="px-5 py-8 text-center text-sm">
                    <span className="text-green-600 dark:text-green-400 font-medium">All clear</span>
                    <p className="text-muted-foreground text-xs mt-1">No expiring items in the next 90 days</p>
                  </div>
                ) : expiringItems.slice(0, 8).map((item, i) => (
                  <div key={i} className="px-5 py-2.5 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.pilotName}</p>
                      <p className="text-xs text-muted-foreground">{item.description}</p>
                    </div>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      item.severity === 'expired' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                      item.severity === 'critical' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
                      item.severity === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    }`}>
                      {item.daysRemaining < 0 ? 'Expired' : `${item.daysRemaining}d`}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Fleet / Active MELs */}
            <div className="bg-card border border-border rounded-lg">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <WrenchScrewdriverIcon className="w-5 h-5 text-amber-500" />
                  Active MEL Deferrals
                </h2>
                {activeMels.length > 0 && (
                  <button onClick={() => navigate('/mel/active')} className="text-xs text-primary hover:text-primary/80 font-medium flex items-center gap-0.5">
                    View all <ChevronRightIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="divide-y divide-border">
                {activeMels.length === 0 ? (
                  <div className="px-5 py-8 text-center text-sm">
                    <span className="text-green-600 dark:text-green-400 font-medium">Fleet clear</span>
                    <p className="text-muted-foreground text-xs mt-1">No active MEL deferrals</p>
                  </div>
                ) : activeMels.slice(0, 5).map((mel) => (
                  <div key={mel.id} className="px-5 py-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">
                        {mel.aircraft?.tailNumber || `Aircraft #${mel.aircraftId}`}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                        Cat {mel.melItem.repairCategory}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {mel.melItem.itemNumber} — {mel.melItem.title}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-card border border-border rounded-lg px-5 py-4">
              <h2 className="font-semibold text-foreground text-sm mb-3">Summary</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total trips</span>
                  <span className="font-medium text-foreground">{trips.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Completed</span>
                  <span className="font-medium text-foreground">{completedTrips.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total flight legs</span>
                  <span className="font-medium text-foreground">{trips.reduce((s, t) => s + t.flights.length, 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Active MELs</span>
                  <span className="font-medium text-foreground">{activeMels.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, subtitle, color, onClick }: {
  icon: React.ReactNode;
  label: string;
  value: number;
  subtitle?: string;
  color: 'green' | 'amber' | 'red' | 'blue';
  onClick?: () => void;
}) {
  const colors = {
    green: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
  };

  const iconColors = {
    green: 'text-green-600 dark:text-green-400',
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
    blue: 'text-blue-600 dark:text-blue-400',
  };

  return (
    <button
      onClick={onClick}
      className={`${colors[color]} rounded-lg p-4 text-left transition-all hover:shadow-md hover:scale-[1.02] active:scale-100`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className={iconColors[color]}>{icon}</span>
        <span className="text-3xl font-bold">{value}</span>
      </div>
      <p className="text-sm font-medium">{label}</p>
      {subtitle && <p className="text-xs opacity-75 mt-0.5">{subtitle}</p>}
    </button>
  );
}
